/**
 * Playlist feature constants — transitions, speeds, scale options, limits, and colors.
 */
import {
  SCALE_TYPES,
  FILE_TYPES,
  APP_TYPES,
  WEB_TYPES,
  MEDIA_TYPES,
  PLAYLIST_SPECIAL_APP_TYPES,
} from '@scio3/shared-types';
import { getAliasType } from '../../assets/utils/app-meta';
import type { PlaylistAsset } from '@scio3/shared-types';

export const TRANSITION_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slideFromRight', label: 'Slide from Right' },
  { value: 'slideFromLeft', label: 'Slide from Left' },
  { value: 'rotateIn', label: 'Rotate In' },
  { value: 'flip', label: 'Flip' },
  { value: 'gallery', label: 'Gallery' },
] as const;

export type TransitionType = (typeof TRANSITION_TYPES)[number]['value'];

export const TRANSITION_SPEEDS = [
  { value: 'slow', label: 'Slow' },
  { value: 'medium', label: 'Medium' },
  { value: 'fast', label: 'Fast' },
  { value: 'custom', label: 'Custom' },
] as const;

export type TransitionSpeed = (typeof TRANSITION_SPEEDS)[number]['value'];

export const SCALE_OPTIONS = [
  { value: SCALE_TYPES.NONE, label: 'None' },
  { value: SCALE_TYPES.FIT, label: 'Fit' },
  { value: SCALE_TYPES.STRETCH, label: 'Stretch' },
  { value: SCALE_TYPES.ZOOM, label: 'Zoom' },
] as const;

export const ORIENTATION_OPTIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'both', label: 'Both' },
] as const;

export const PLAYLIST_COLORS = [
  '#1C84C6',
  '#81D4FA',
  '#1AB394',
  '#ED5565',
  '#F8AC59',
  '#795548',
] as const;

export const PLAY_EVERY_OPTIONS = [
  { value: 15, label: 'Every 15 min' },
  { value: 30, label: 'Every 30 min' },
  { value: 60, label: 'Every 1 hour' },
  { value: 180, label: 'Every 3 hours' },
  { value: 360, label: 'Every 6 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
] as const;

/** Apply-to values for content targeting (adv. playback settings).
 * Values are GraphQL enum keys from PLAYLIST_MESSAGE_APPLY_TO (backend maps PLAY_ON→'in', NOT_PLAY_ON→'nin'). */
export const PLAYLIST_MESSAGE_APPLY_TO = {
  ALL: 'null',
  PLAY_ON: 'PLAY_ON',
  NOT_PLAY_ON: 'NOT_PLAY_ON',
} as const;

/** Device type options for content targeting.
 * Values are GraphQL enum keys from PLAYLIST_APPLY_TO_DATA_TYPE (backend maps DEVICE→'device', TAG→'tag', ALL_SCREEN→'all'). */
export const PLAYLIST_MESSAGE_DEVICE_TYPE = {
  DEVICE: 'DEVICE',
  TAG: 'TAG',
  ALL_SCREEN: 'ALL_SCREEN',
} as const;

/** Default custom play-every value in minutes (when user picks "Custom") */
export const DEFAULT_CUSTOM_PLAY_EVERY_VALUE = 120;

/** Normalize apply-to value from backend — may arrive as lowercase DB value ('in'/'nin') or UPPERCASE GraphQL enum key. */
export function normalizeApplyToValue(value: string): string {
  const map: Record<string, string> = {
    in: PLAYLIST_MESSAGE_APPLY_TO.PLAY_ON,
    nin: PLAYLIST_MESSAGE_APPLY_TO.NOT_PLAY_ON,
  };
  return map[value] ?? value;
}

/** Normalize device type value from backend — may arrive as lowercase DB value ('device'/'tag'/'all') or UPPERCASE GraphQL enum key. */
export function normalizeDeviceTypeValue(value: string): string {
  const map: Record<string, string> = {
    device: PLAYLIST_MESSAGE_DEVICE_TYPE.DEVICE,
    tag: PLAYLIST_MESSAGE_DEVICE_TYPE.TAG,
    all: PLAYLIST_MESSAGE_DEVICE_TYPE.ALL_SCREEN,
  };
  return map[value] ?? value;
}

/** Resume on next play options for item options */
export const RESUME_ON_NEXT_PLAY_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'true', label: 'ON' },
  { value: 'false', label: 'OFF' },
] as const;

/** Target tags mode options */
export const TARGET_TAGS_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'custom', label: 'Custom' },
] as const;

/** Target tags mode options for playlist-parent type */
export const TARGET_TAGS_PLAYLIST_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'useAsset', label: 'Use Asset Default' },
] as const;

/** Apply-to options for adv. playback settings.
 * '__all__' is the sentinel for no apply-to filter (maps to empty string in applyToData). */
export const APPLY_TO_OPTIONS = [
  { value: '__all__', label: 'All' },
  { value: PLAYLIST_MESSAGE_APPLY_TO.PLAY_ON, label: 'Play on' },
  { value: PLAYLIST_MESSAGE_APPLY_TO.NOT_PLAY_ON, label: 'Not play on' },
] as const;

/** Target screen type options (Screens or Tags — required when applyTo is set). */
export const TARGET_SCREEN_TYPE_OPTIONS = [
  { value: PLAYLIST_MESSAGE_DEVICE_TYPE.DEVICE, label: 'Screens' },
  { value: PLAYLIST_MESSAGE_DEVICE_TYPE.TAG, label: 'Tags' },
] as const;

/**
 * Map frontend camelCase targetTagsDefault values → GraphQL UPPERCASE enum values.
 * Backend enum: PLAYLIST_TARGET_TAGS { USE_PLAYLIST_DEFAULT = "usePlDefault", USE_ASSET = "useAsset" }
 * Read path normalizes UPPERCASE → camelCase (PlaylistOptionsDialog buildInitialSubPlaylistData).
 * Write path must reverse-map back to UPPERCASE for mutations.
 */
export const TARGET_TAGS_DEFAULT_TO_GQL: Record<string, string> = {
  usePlDefault: 'USE_PLAYLIST_DEFAULT',
  useAsset: 'USE_ASSET',
  custom: 'CUSTOM',
};

/**
 * App types that behave like sub-playlists (no transition/speed, has sub-playlist control).
 * Matches scio 2.0's ASSET_APP_TYPE_PLAYLIST_SPECIAL from @optisigns/common-constant.
 *
 * Single source of truth: `PLAYLIST_SPECIAL_APP_TYPES` in @scio3/shared-types. This is a
 * local alias kept for the playlist-feature naming convention — do NOT redefine the list
 * here, or the inline lock (isDurationEditLocked) and bulk lock (EditDurationDialog) drift.
 */
export const ASSET_APP_TYPE_PLAYLIST_SPECIAL = PLAYLIST_SPECIAL_APP_TYPES;

/**
 * Whether an appType behaves like a sub-playlist (MRSS, Google Drive, Dropbox, OneDrive,
 * contentRotation) — i.e. its content is a backing playlist, not a renderable single asset.
 * Shared membership test so preview / push-to-screens / duration-lock stay in lockstep.
 *
 * Presence-gated for Power BI, mirroring the backend's `isPlaylistBackedAsset`: Power BI is
 * the first PLAYLIST_SPECIAL-shaped app type with two shapes in the wild — a legacy
 * single-page asset (no `playlistId`) and a multi-page container (has one) — so membership
 * alone would not be enough for it. The gate is scoped to Power BI only, NOT applied to all
 * five types currently in `PLAYLIST_SPECIAL_APP_TYPES`: those five have existing 2-arg call
 * sites all over the codebase (duration lock in the playlist editor, bulk duration edit, …)
 * that this task does not touch, and none of them pass a `playlistId` today. Requiring
 * presence universally would silently flip every one of those call sites from
 * "locked/special" to "not locked/not special" for every existing
 * Drive/MRSS/OneDrive/Dropbox/ContentRotation asset. Scoping to Power BI keeps their 2-arg
 * behaviour exactly as it was.
 *
 * **The Power BI branch below is currently INERT / dead code, on purpose.**
 * `WEB_TYPES.MICROSOFT_BI` is deliberately not yet in `PLAYLIST_SPECIAL_APP_TYPES` (see that
 * array's comment in `@scio3/shared-types` app-types.ts for why), so the array-membership
 * guard above returns `false` for `'microsoft-bi'` before this function ever reaches the
 * `appType === WEB_TYPES.MICROSOFT_BI` branch below. Kept in place, with the optional
 * `playlistId` param already plumbed through the two call sites that pass it
 * (`PushToScreensDialog`, `ConfigEngineDialog`), so this presence-gating infrastructure is
 * ready to activate the moment the array is widened. Do not delete this branch thinking it's
 * unused, and do not mistake it for live Power BI behaviour in the meantime.
 */
export function isPlaylistSpecialAppType(
  appType?: string | null,
  playlistId?: string | null,
): boolean {
  if (!appType || !(ASSET_APP_TYPE_PLAYLIST_SPECIAL as readonly string[]).includes(appType)) {
    return false;
  }
  if (appType === WEB_TYPES.MICROSOFT_BI) {
    return !!playlistId;
  }
  return true;
}

/** Asset app types that allow resumeOnNextPlay */
export const RESUME_ON_NEXT_PLAY_ASSET_FILE_TYPES = [
  FILE_TYPES.VIDEO,
  FILE_TYPES.DOCUMENT,
] as const;
export const RESUME_ON_NEXT_PLAY_ASSET_WEB_TYPES = ['youtube', 'vimeo'] as const;
export const RESUME_ON_NEXT_PLAY_ASSET_TYPES = ['splitScreen'] as const;

/* ── Per-item Mute eligibility (#1913) ───────────────────────────────────────
 *
 * The player treats "this item is a video" as "this item has audio", so with a screen's
 * Sound Priority set to "Playlist Video" a SILENT video (e.g. a Canva-exported MP4 Feed) still
 * takes audio priority and ducks the background music to dead air. `isMute` lets the user say
 * per item "this one has nothing to hear", so the music keeps playing over it.
 *
 * The control is therefore offered exactly where the player might hand an item audio priority.
 * Muting an image or a plain web page would be a no-op control, so those stay hidden.
 *
 * #2112 applied that rule to the whole list, against both players' source. The test is not "does
 * this type have sound" but "does the code that renders it READ the per-item flag". Eight types
 * failed it and were removed: `youtubeLive`, `streamingApp`, `livestream`, `facebookVideo`,
 * `facebookStreaming`, `twitch`, `screenShare` and `AeriCast`. All eight route into a generic
 * WebView — Android `WebAppSlideFragment` (whose only conditional mute is Vimeo-only, ~:857) and
 * Electron `Website.electron.tsx` (whose `setAudioMuted` at :782/:1350 reads only
 * `device.muted || backgroundAudioEnabled`, never `context.muted`). Several of them are declared
 * as constants in the players (`AppType.FACEBOOK_LIVE`, `TWITCH`, …) but never referenced in
 * routing, which is what made them look supported.
 *
 * Removing a type does NOT strand an already-muted item: `isMute` stays in the document but is
 * inert on these paths, and `PlaylistItemRow`'s Muted badge is gated on this same helper so the
 * row and the dialog always agree.
 */

/** File types that can carry an audio track. */
export const MUTABLE_ASSET_FILE_TYPES = [FILE_TYPES.VIDEO] as const;

/**
 * Web types the player renders through a video/stream surface.
 *
 * `YOUTUBE_LIVE` and `STREAMING_APP` were removed in #2112: neither player has a renderer for
 * them. Android's `LoadDataFactory.createWebTask` matches `WebType.YOUTUBE` case-sensitively, so
 * `youtubeLive` falls through to `LoadWebAppTask`; Electron's `Asset/index.tsx` has no branch for
 * either value, so both reach the `webLink` catch-all and render `<Website>`.
 */
export const MUTABLE_ASSET_WEB_TYPES = [
  WEB_TYPES.YOUTUBE,
  WEB_TYPES.VIMEO,
  // Electron honours this one (`Asset/index.tsx` matches `webType === VIDEO` into `Video`);
  // Android does NOT — a direct video URL lands in `WebAppSlideFragment`, whose only mute is
  // Vimeo-only. Kept because the control is real for the Electron fleet; the Android gap is a
  // player bug, not a reason to take the control away from Windows/Mac screens.
  WEB_TYPES.VIDEO,
] as const;

/**
 * Video-backed app types.
 *
 * FEED is the one this ticket exists for. It is safe to include even though a Feed is really a
 * backing playlist, because the delivery layer keeps the ITEM: rest-api `formatAssetFeed` rewrites
 * the Feed into a one-zone `splitScreen` and `Object.assign`s it back onto the original item, so
 * item-level fields such as `isMute` survive to the player payload.
 *
 * VIDEO_STREAMING and RTSP_STREAM (#2108) were missing from the first cut only because they were
 * not yet mirrored in `APP_TYPES` — they are plain video-surface apps, not containers. Both players
 * render them through a shared video player that reads the item flag: Electron folds `isMute` into
 * `PositionContext.muted` in `Asset/index.tsx` before the `VIDEO_APP_TYPES` branch, and Android's
 * `BaseRemoteSlideFragment.setupVideo` (plus `RtspStreamSlideFragment`'s override) calls
 * `setVolume(isMute() ? 0 : 1)`. So the control is live on both, not just offered.
 *
 * EMERGENCY_FEED was here and was removed (#2112). The reason it was kept — "same as Feed" —
 * turned out to be false: rest-api gates `formatAssetFeed` on `feed` (`app/utils.js:980`), and
 * `emergencyFeed` never reaches it. It is delivered as a plain web asset, getting only `deviceId`
 * appended to its `webLink` (`app/utils.js:1431`, the same branch as weather and CAPAlert), so
 * there is no splitScreen wrapper and no parent → zone → child mute to inherit.
 *
 * On that web path the two players disagree: Electron's `Website.electron.tsx` reads only
 * `device.muted || backgroundAudioEnabled` and ignores the item flag, while Android honours it
 * since optisigns-android!53 muted the media elements in `WebAppSlideFragment`. A control that
 * works on one fleet and not the other is the Video URL situation in reverse, and the call on this
 * type was to not offer it at all. Do not re-add it without the Electron half.
 *
 * Deliberately NOT here: the container types in `PLAYLIST_SPECIAL_APP_TYPES` (MRSS, the cloud
 * drives, Content Rotation) and nested playlists. rest-api `formatPlaylist` REPLACES those items
 * with their flattened children, copying down only the fields it names explicitly (`playEvery`,
 * `resumeOnNextPlay`, `subPlaylistControl`), so a parent-level `isMute` is dropped before it ever
 * reaches a device — the control would silently do nothing. Their child items each carry their own
 * `isMute` through the flattening, which is the case users actually hit. If parent-level mute is
 * wanted later, teach `formatPlaylist` to propagate it first, then add the types here.
 */
export const MUTABLE_ASSET_APP_TYPES = [
  APP_TYPES.FEED,
  // Electron honours this (`LiveTv.tsx:242` reads `context.muted`). Android has NO occurrence of
  // `liveTv` at all, so its path there is unproven rather than known-broken. Kept for the same
  // reason as WEB_TYPES.VIDEO above.
  APP_TYPES.LIVE_TV,
  APP_TYPES.VIDEO_STREAMING,
  APP_TYPES.RTSP_STREAM,
] as const;

/**
 * Whether the per-item Mute control applies to this playlist item.
 *
 * Order matters: the container check runs FIRST, because a Content Rotation item can also carry a
 * video-ish `fileType` copied from its backing asset and would otherwise slip through.
 */
export function canMutePlaylistItem(item?: PlaylistAsset | null): boolean {
  if (!item) return false;

  // Containers — flattened at delivery, parent-level mute would be dropped. See above.
  if (item.type === MEDIA_TYPES.PLAYLIST) return false;
  if (isPlaylistSpecialAppType(item.appType)) return false;

  if ((MUTABLE_ASSET_APP_TYPES as readonly string[]).includes(item.appType ?? '')) return true;
  if ((MUTABLE_ASSET_WEB_TYPES as readonly string[]).includes(item.webType ?? '')) return true;
  return (MUTABLE_ASSET_FILE_TYPES as readonly string[]).includes(item.fileType ?? '');
}

/** Playlist options defaults — matches scio1 initialization values */
export const DEFAULT_SCALE = SCALE_TYPES.FIT;
export const DEFAULT_TRANSITION = 'fade';
export const DEFAULT_SPEED = 'medium';
export const DEFAULT_SPEED_VALUE = 1.0;
export const DEFAULT_ORIENTATION = 'NONE';
export const DEFAULT_BACKGROUND_AUDIO_TYPE = 'none';
/** normalDuration for fade transition (ms) — scio1: 1500 */
export const NORMAL_DURATION_FADE = 1500;
/** normalDuration for non-fade transitions (ms) — scio1: 2000 */
export const NORMAL_DURATION_DEFAULT = 2000;
/** Speed slider range */
export const SPEED_SLIDER_MIN = 0.1;
export const SPEED_SLIDER_MAX = 2.0;
export const SPEED_SLIDER_STEP = 0.1;

export const MIN_DURATION = 4;
export const MAX_DURATION = 9999;
export const MAX_DURATION_SUB_PLAYLIST = 86400;
export const DEFAULT_DURATION = 20;

/** Characters in the longest displayed duration (2-decimal rounding), so every row's number can
 *  take that width and the "sec" labels and edit pencils line up down the column. */
export function getDurationChars(items: ReadonlyArray<{ duration?: number | null }>): number {
  return items.reduce(
    (max, it) =>
      Math.max(max, String(Math.round((it.duration ?? DEFAULT_DURATION) * 100) / 100).length),
    1,
  );
}

/**
 * A Power BI multi-page container — the app type AND a backing playlist, the same rule as
 * open-api's `isPlaylistBackedAsset` (`src/app/shared/playlist-special.ts`). Do not re-derive it
 * differently.
 *
 * This exists instead of adding `WEB_TYPES.MICROSOFT_BI` to `PLAYLIST_SPECIAL_APP_TYPES` for the
 * reason recorded on that array (app-types.ts) and on `isPlaylistSpecialAppType` above. Consumed
 * by `isDurationEditLocked` below and by `isComputedPowerBIDuration`, so the blast radius is the
 * playlist editor's duration column and the add-time duration. When the array is eventually
 * widened, the lock use becomes redundant and should be deleted in the same change.
 */
export function isPowerBIContainer(appType?: string | null, playlistId?: string | null): boolean {
  return appType === WEB_TYPES.MICROSOFT_BI && !!playlistId;
}

/**
 * A Power BI asset whose stored `duration` is a COMPUTED page total (a multi-page container, or a
 * single-page asset since the 2026-10-14 change) rather than a stamp. Such an asset keeps its stored
 * duration on add even though Power BI has an app default. Mirrors open-api
 * `playlist-duration.ts isComputedPowerBIDuration` — the two must move together. The backend also
 * reads `pages`; no FE query selects it, so it is not accepted here.
 */
export function isComputedPowerBIDuration(
  asset:
    | {
        appType?: string | null;
        webType?: string | null;
        pageCount?: number | null;
        playlistId?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!asset) return false;
  // Alias type, the same key lookupPlaylistDefaultDuration uses: a webType-only Power BI asset
  // gets the app default, so it must also be recognised here.
  const aliasType = getAliasType({
    appType: asset.appType ?? undefined,
    webType: asset.webType ?? undefined,
  });
  if (aliasType !== WEB_TYPES.MICROSOFT_BI) return false;
  return Number(asset.pageCount) > 0 || isPowerBIContainer(aliasType, asset.playlistId);
}

/*
 * `shouldIgnoreAppStoredDuration()` lived here until #1967.
 *
 * It replaced an app asset's stored duration with the account default on add (#1802 req 4).
 * Product (19 Aug) locked the resolution order as
 *   Asset duration > appMeta > account-level default > global default (20)
 * and the order, including the cases that still ignore a stored stamp, now lives entirely in
 * `resolvePlaylistItemDuration`.
 *
 * Removed rather than left returning false, so nobody wires it back in. See open-api
 * `playlist-duration.ts` for the same change on the persisting side.
 */

/**
 * Whether a playlist item's duration is locked (read-only) in the editor.
 * Mirrors scio1's duration-input disable conditions (editSection.html).
 *
 * Locked when EITHER:
 *  1. Special app type (MRSS, cloud drives, Content Rotation) — backed by a
 *     system-managed hidden playlist whose totalDuration is recomputed by
 *     background jobs (e.g. the MRSS feed job's `updatePlaylistParent()`
 *     overwrites the parent-item `duration` with the feed total on every fetch,
 *     so a user-set value silently reverts — the reported bug). scio1 disables
 *     this via `ASSET_APPTYPE_PLAYLIST_SPECIAL.includes(appType)`.
 *  2. `duration === 0` — scio1 disables the input when `asset.duration == 0`
 *     (applies to BOTH non-playlist and playlist-type inputs). New items added
 *     in scio3 are clamped to >= MIN_DURATION, but legacy items loaded from the
 *     DB can carry duration 0. Only an exact 0 locks; undefined/null does not
 *     (those fall back to DEFAULT_DURATION and stay editable).
 *
 * NOT applied to real nested playlists with a positive duration (`type ===
 * 'playlist'`, no special appType): they stay user-editable when
 * subPlaylistControl is off, matching scio1's playlist-type input.
 *
 * `playlistId` is optional and presence-gates Power BI only (see `isPlaylistSpecialAppType`)
 * — existing 2-arg callers keep the exact behaviour they had before this app type existed.
 */
export function isDurationEditLocked(
  appType: string | undefined | null,
  duration?: number | null,
  playlistId?: string | null,
): boolean {
  if (duration === 0) return true;
  // Locked for the same reason as the five array types: the device expands the container into its
  // children and ignores this number entirely, so an editable field can only mislead.
  if (isPowerBIContainer(appType, playlistId)) return true;
  return isPlaylistSpecialAppType(appType, playlistId);
}

/** CDN URL for nested playlist thumbnails */
export const PLAYLIST_THUMBNAIL_URL = `${import.meta.env.VITE_CDN_URL || 'https://cdn.optisigns.com'}/scio/images/playlist.png`;
export const AUTO_SAVE_DEBOUNCE = 3000;
/** Waits before each automatic retry of a save that failed to reach the server (#2541) */
export const AUTO_SAVE_RETRY_DELAYS = [2000, 5000, 15000];
export const MAX_HISTORY_DEPTH = 20;
/** Bulk remove threshold: below this count, use debounced auto-save; at or above, call removePlaylistItems API directly. */
export const BULK_REMOVE_API_THRESHOLD = 50;
export const SEARCH_DEBOUNCE = 300;
export const DEFAULT_PAGE_SIZE = 50;
/** Fetch all items — matches scio2 PAGE_SETTING.LOAD_ALL. Used when resolving folder contents. */
export const LOAD_ALL_LIMIT = 99999;
